"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateFundingPool = exports.calculateLongShortFundingRate = exports.calculateEstimatedFundingRate = exports.calculateAllEstimatedFundingRate = void 0;
const anchor_1 = require("@project-serum/anchor");
const numericConstants_1 = require("../constants/numericConstants");
const market_1 = require("./market");
/**
 *
 * @param market
 * @param pythClient
 * @param periodAdjustment
 * @returns Estimated funding rate. : Precision //TODO-PRECISION
 */
function calculateAllEstimatedFundingRate(market, pythClient, periodAdjustment = new anchor_1.BN(1)) {
    return __awaiter(this, void 0, void 0, function* () {
        // periodAdjustment
        // 	1: hourly
        //  24: daily
        //  24 * 365.25: annualized
        const secondsInHour = new anchor_1.BN(3600);
        const hoursInDay = new anchor_1.BN(24);
        if (!market.initialized) {
            return [new anchor_1.BN(0), new anchor_1.BN(0), new anchor_1.BN(0)];
        }
        const payFreq = new anchor_1.BN(market.amm.fundingPeriod);
        const oraclePriceData = yield pythClient.getPriceData(market.amm.oracle);
        const oracleTwapWithMantissa = new anchor_1.BN(oraclePriceData.twap.value * numericConstants_1.MARK_PRICE_PRECISION.toNumber());
        const now = new anchor_1.BN((Date.now() / 1000).toFixed(0));
        const timeSinceLastUpdate = now.sub(market.amm.lastFundingRateTs);
        const lastMarkTwapWithMantissa = market.amm.lastMarkPriceTwap;
        const lastMarkPriceTwapTs = market.amm.lastMarkPriceTwapTs;
        const timeSinceLastMarkChange = now.sub(lastMarkPriceTwapTs);
        const markTwapTimeSinceLastUpdate = lastMarkPriceTwapTs.sub(market.amm.lastFundingRateTs);
        const baseAssetPriceWithMantissa = market_1.calculateMarkPrice(market);
        const markTwapWithMantissa = markTwapTimeSinceLastUpdate
            .mul(lastMarkTwapWithMantissa)
            .add(timeSinceLastMarkChange.mul(baseAssetPriceWithMantissa))
            .div(timeSinceLastMarkChange.add(markTwapTimeSinceLastUpdate));
        const twapSpread = markTwapWithMantissa.sub(oracleTwapWithMantissa);
        const twapSpreadPct = twapSpread
            .mul(numericConstants_1.MARK_PRICE_PRECISION)
            .mul(new anchor_1.BN(100))
            .div(oracleTwapWithMantissa);
        const lowerboundEst = twapSpreadPct
            .mul(payFreq)
            .mul(anchor_1.BN.min(secondsInHour, timeSinceLastUpdate))
            .mul(periodAdjustment)
            .div(secondsInHour)
            .div(secondsInHour)
            .div(hoursInDay);
        const interpEst = twapSpreadPct.mul(periodAdjustment).div(hoursInDay);
        const interpRateQuote = twapSpreadPct.mul(periodAdjustment).div(hoursInDay)
            .div(numericConstants_1.MARK_PRICE_PRECISION.div(numericConstants_1.QUOTE_PRECISION));
        let feePoolSize = calculateFundingPool(market);
        if (interpRateQuote.lt(new anchor_1.BN(0))) {
            feePoolSize = feePoolSize.mul(new anchor_1.BN(-1));
        }
        let cappedAltEst;
        let largerSide;
        let smallerSide;
        if (market.baseAssetAmountLong.gt(market.baseAssetAmountShort)) {
            largerSide = market.baseAssetAmountLong.abs();
            smallerSide = market.baseAssetAmountShort.abs();
            if (twapSpread.gt(new anchor_1.BN(0))) {
                return [lowerboundEst, interpEst, interpEst];
            }
        }
        else if (market.baseAssetAmountLong.lt(market.baseAssetAmountShort)) {
            largerSide = market.baseAssetAmountShort.abs();
            smallerSide = market.baseAssetAmountLong.abs();
            if (twapSpread.lt(new anchor_1.BN(0))) {
                return [lowerboundEst, interpEst, interpEst];
            }
        }
        else {
            return [lowerboundEst, interpEst, interpEst];
        }
        if (largerSide.gt(numericConstants_1.ZERO)) {
            cappedAltEst = smallerSide.mul(twapSpread).div(largerSide);
            const feePoolTopOff = feePoolSize.mul(numericConstants_1.MARK_PRICE_PRECISION.div(numericConstants_1.QUOTE_PRECISION))
                .mul(numericConstants_1.AMM_RESERVE_PRECISION).div(largerSide);
            cappedAltEst = cappedAltEst.add(feePoolTopOff);
            cappedAltEst = cappedAltEst.mul(numericConstants_1.MARK_PRICE_PRECISION)
                .mul(new anchor_1.BN(100))
                .div(oracleTwapWithMantissa)
                .mul(periodAdjustment).div(hoursInDay);
            if (cappedAltEst.abs().gt(interpEst.abs())) {
                cappedAltEst = interpEst;
            }
        }
        else {
            cappedAltEst = interpEst;
        }
        return [lowerboundEst, cappedAltEst, interpEst];
    });
}
exports.calculateAllEstimatedFundingRate = calculateAllEstimatedFundingRate;
/**
 *
 * @param market
 * @param pythClient
 * @param periodAdjustment
 * @param estimationMethod
 * @returns Estimated funding rate. : Precision //TODO-PRECISION
 */
function calculateEstimatedFundingRate(market, pythClient, periodAdjustment = new anchor_1.BN(1), estimationMethod) {
    return __awaiter(this, void 0, void 0, function* () {
        const [lowerboundEst, cappedAltEst, interpEst] = yield calculateAllEstimatedFundingRate(market, pythClient, periodAdjustment);
        if (estimationMethod == 'lowerbound') {
            //assuming remaining funding period has no gap
            return lowerboundEst;
        }
        else if (estimationMethod == 'capped') {
            return cappedAltEst;
        }
        else {
            return interpEst;
        }
    });
}
exports.calculateEstimatedFundingRate = calculateEstimatedFundingRate;
/**
 *
 * @param market
 * @param pythClient
 * @param periodAdjustment
 * @param estimationMethod
 * @returns Estimated funding rate. : Precision //TODO-PRECISION
 */
function calculateLongShortFundingRate(market, pythClient, periodAdjustment = new anchor_1.BN(1)) {
    return __awaiter(this, void 0, void 0, function* () {
        const [_, cappedAltEst, interpEst] = yield calculateAllEstimatedFundingRate(market, pythClient, periodAdjustment);
        if (market.baseAssetAmountLong.gt(market.baseAssetAmountShort)) {
            return [cappedAltEst, interpEst];
        }
        else if (market.baseAssetAmountLong.lt(market.baseAssetAmountShort)) {
            return [interpEst, cappedAltEst];
        }
        else {
            return [interpEst, interpEst];
        }
    });
}
exports.calculateLongShortFundingRate = calculateLongShortFundingRate;
/**
 *
 * @param market
 * @returns Estimated fee pool size
 */
function calculateFundingPool(market) {
    const totalFeeLB = market.amm.totalFee.div(new anchor_1.BN(2));
    const feePool = market.amm.totalFeeMinusDistributions.sub(totalFeeLB);
    // return new BN(QUOTE_PRECISION.mul(new BN(2400)));
    return feePool;
}
exports.calculateFundingPool = calculateFundingPool;
